// @ts-ignore
import React from "react";
import { Suspense } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";

import Button from "@atlaskit/button";
import { token } from "@atlaskit/tokens";

import { messages } from "../messages";
import { fontFamily } from "../util";

import { WACAvatar } from "./avatar";
import { LoadingSpinner } from "./spinner";
import { UserDetailsCard } from "./user-details-card";

const DropdownMenu = React.lazy(() =>
    // @ts-ignore
    import("./dropdown-import").then(({ DropdownMenu }) => ({
        default: DropdownMenu
    }))
);

const DropdownItem = React.lazy(() =>
    import("@atlaskit/dropdown-menu").then(({ DropdownItem }) => ({
        default: DropdownItem
    }))
);

const DropdownItemGroup = React.lazy(() =>
    import("@atlaskit/dropdown-menu").then(({ DropdownItemGroup }) => ({
        default: DropdownItemGroup
    }))
);

export interface LoggedInUserProps {
    userInfo: {
        readonly displayName: string;
        readonly email: string;
        readonly avatar: string;
    };
    readonly onLogOutClick: () => void;
    readonly onManageLicensesClick: () => void;
    readonly onManageProfileClick: () => void;
    readonly onSwitchAccountClick: () => void;
}

export const LoggedInUser = ({
    userInfo,
    onLogOutClick,
    onManageLicensesClick,
    onManageProfileClick,
    onSwitchAccountClick
}: LoggedInUserProps) => {
    return (
        <Suspense fallback={LoadingSpinner}>
            <div data-testid="main-container" css={mainContainer}>
                <DropdownMenu
                    spacing="compact"
                    placement="bottom-end"
                    // @ts-ignore
                    trigger={({ triggerRef, ...props }) => (
                        <Button
                            className="avatar-button"
                            css={overrideAvatarButtonStyle}
                            {...props}
                            iconBefore={
                                <WACAvatar
                                    name={userInfo.displayName}
                                    src={userInfo.avatar}
                                    size={"small"}
                                    showTooltip={true}
                                />
                            }
                            ref={triggerRef}
                        />
                    )}
                >
                    <div
                        css={loggedInUserContainer}
                        data-testid="global-nav-user-profile-container-user-loggedin"
                    >
                        <DropdownItemGroup>
                            <DropdownItem isDisabled={true}>
                                <UserDetailsCard
                                    name={userInfo.displayName}
                                    src={userInfo.avatar}
                                    email={userInfo.email}
                                    size={"small"}
                                />
                            </DropdownItem>
                            <DropdownItem onClick={onSwitchAccountClick}>
                                <FormattedMessage {...messages.switchAccount} />
                            </DropdownItem>
                            <DropdownItem onClick={onManageProfileClick}>
                                <FormattedMessage {...messages.profile} />
                            </DropdownItem>
                            <DropdownItem onClick={onManageLicensesClick}>
                                <FormattedMessage {...messages.licenses} />
                            </DropdownItem>
                            <DropdownItem onClick={onLogOutClick}>
                                <FormattedMessage {...messages.logOut} />
                            </DropdownItem>
                        </DropdownItemGroup>
                    </div>
                </DropdownMenu>
            </div>
        </Suspense>
    );
};

const mainContainer = css`
    font-family: ${fontFamily};

    :hover,
    :focus,
    :active {
        opacity: 0.8;
    }
`;
const loggedInUserContainer = css`
    font-family: ${fontFamily};
    width: 250px;
    line-height: ${token("space.150")};

    div::before {
        content: none;
    }

    button[disabled] {
        padding: ${token("space.0")};
        cursor: auto;
    }

    button[role="menuitem"] {
        font-family: ${fontFamily};
        font-size: ${token("space.150")};
    }
`;

const overrideAvatarButtonStyle = css`
    padding: ${token("space.0")};
    width: ${token("space.250")};
    height: ${token("space.250")};
    border-radius: 50%;
`;
